import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService } from 'primeng/api';
import { NotificationTypes } from 'src/app/app.enums';
import { AuthConfig, AuthService, ResponseObject } from 'src/app/Services/auth.service';
import { NotificationsService } from 'src/app/Services/shared/notifications.service';
import { WixService, WixSiteDetail } from 'src/app/Services/Wix-Service/wix.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'app-wix',
    templateUrl: './wix.component.html',
    inputs: ['isSentByEditProfile'],
    standalone: false
})
export class WixComponent implements OnInit {
  public wixSiteDetail: WixSiteDetail | undefined;
  public isConnectWixSuccessDialogVisible: boolean = false;
  public isSentByEditProfile: boolean = false;

  constructor(
    private wixService: WixService,
    private ngxSpinnerService: NgxSpinnerService,
    private notificationsService: NotificationsService,
    private authService: AuthService,
    private confirmationService: ConfirmationService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params['token']) {
        this.onWixAppInstallDoneConnectWithWix(params['token']);
      } 
      else if (params['code'] && params['instanceId'] && params['state']) {
        this.onWixAccountCallBack(params['code'], params['instanceId'], params['state']);
      } 
      else {
        if (this.isSentByEditProfile == false) {
          this.getWixAccountDetails();
        }
      }
    });
  }

  public onWixAppInstallDoneConnectWithWix(token: any): void {
    this.ngxSpinnerService.show();
    
    this.wixService.connectWithWixAccountApi(token).subscribe(
      (response) => {
        this.ngxSpinnerService.hide();
        
        if (response.Success) {
          window.location.href = response.ResponseData;
        } else {
          console.error('Unexpected response:', response);
        }
      },
      (error) => {
        this.ngxSpinnerService.hide();
        console.error('Failed to connect to Wix account:', error);
        
        this.notificationsService.showNotification(
          this.transloco.translate('wix.connectionFailedTitle'),
          this.transloco.translate('wix.connectionFailedMessage'),
          NotificationTypes.Error
        );
      }
    );
  }

  public connectWithWixAccount(): void {
    this.ngxSpinnerService.show();
    
    this.wixService.wixAppInstallationLinkToConnectWithWix().subscribe(
      (response) => {
        this.ngxSpinnerService.hide();
        
        if (response.Success) {
          window.location.href = response.ResponseData;
        } else {
          console.error('Unexpected response:', response);
        }
      },
      (error) => {
        this.ngxSpinnerService.hide();
        console.error('Failed to connect to Wix account:', error);
        
        this.notificationsService.showNotification(
          this.transloco.translate('wix.connectionFailedTitle'),
          this.transloco.translate('wix.connectionFailedMessage'),
          NotificationTypes.Error
        );
      }
    );
  }

  public onDisconnectWixAccount(): void {
    this.confirmationService.confirm({
      message: this.transloco.translate('wix.confirmDisconnect'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.ngxSpinnerService.show();
        
        this.wixService.onDisconnectWixAccount().subscribe(
          (response) => {
            this.ngxSpinnerService.hide();
            
            if (response.Success) {
              this.wixSiteDetail = undefined;
              
              this.notificationsService.showNotification(
                this.transloco.translate('wix.disconnectedTitle'),
                this.transloco.translate('wix.disconnectedMessage'),
                NotificationTypes.Success
              );
            } else {
              console.warn('Unexpected response:', response);
            }
          },
          (error) => {
            this.ngxSpinnerService.hide();
            console.error('Failed to disconnect Wix account:', error);
          }
        );
      },
      reject: () => {
        console.log('Wix account disconnection was cancelled.');
      },
    });
  }

  public getWixAccountDetails(): void {
    this.ngxSpinnerService.show();
    
    this.wixService.getWIXAccountDetails().subscribe(
      (response) => {
        this.ngxSpinnerService.hide();
        
        if (response) {
          this.wixSiteDetail = response;
        } else {
          console.warn('No Wix account details found.');
        }
      },
      (error) => {
        this.ngxSpinnerService.hide();
        console.error('Error fetching Wix account details:', error);
        
        this.notificationsService.showNotification(
          this.transloco.translate('wix.fetchFailedTitle'),
          this.transloco.translate('wix.fetchFailedMessage'),
          NotificationTypes.Error
        );
      }
    );
  }

  public closeWixConnectionSuccessDialog(): void {
    this.isConnectWixSuccessDialogVisible = false;
  }

  public onWixAccountCallBack(code: string, instanceId: string, state: string): void {
    this.ngxSpinnerService.show();
    
    this.wixService.onWixAccountCallBack(code, instanceId, state).subscribe(
      (res: ResponseObject) => {
        this.ngxSpinnerService.hide();
        
        if (res.Success) {
          this.isConnectWixSuccessDialogVisible = true;
          
          this.notificationsService.showNotification(
            this.transloco.translate('wix.successTitle'),
            res.ResponseMessage,
            NotificationTypes.Success
          );
          
          this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {},
            replaceUrl: true,
          });
        } else {
          this.notificationsService.showNotification(
            this.transloco.translate('wix.errorTitle'),
            res.ResponseMessage,
            NotificationTypes.Error
          );
        }
      },
      (error) => {
        this.ngxSpinnerService.hide();
        console.error('Error during Wix account callback:', error);
      }
    );
  }
}